import type { AbstractLeadFormState } from "@component/assets/AbstractLeadForm/AbstractLeadForm";
import { AbstractLeadForm } from "@component/assets/AbstractLeadForm/AbstractLeadForm";
import type { LeadRequest, ProductType } from "@component/assets/AbstractLeadForm/types";
import { formatText } from "@ui/format";
import { JsonDataBuilder } from "@utils/JsonDataBuilder";
import type { EventComponentType } from "@utils/google-analytics-4.type";
import { logError } from "@utils/sentry-error";
import type { ReactNode } from "react";
import React from "react";
import * as Step from "./Steps";
import { DEFAULT_RESPONSE } from "./data";
import { calculate } from "./domain";
import type {
	GreenLoanSmallCalculatorFormFields,
	GreenLoanSmallCalculatorProps,
	GreenLoanSmallCalculatorRequest,
	GreenLoanSmallCalculatorResponse,
} from "./types";
import "./style.scss";

interface State extends AbstractLeadFormState<GreenLoanSmallCalculatorFormFields> {
	errorMessage: string;
	response: GreenLoanSmallCalculatorResponse;
}

/**
 * @Shortcode
 *
 * @title "Zöld hitel kalkulátor"
 * @shortcode "mortgages_green_loan_small"
 * @slug "zold-hitel"
 * @type "calculator"
 */
export class GreenLoanSmallCalculator extends AbstractLeadForm<
	GreenLoanSmallCalculatorRequest,
	GreenLoanSmallCalculatorResponse,
	GreenLoanSmallCalculatorFormFields,
	GreenLoanSmallCalculatorProps,
	State
> {
	// EventParams
	protected readonly productTypes: string = "MORTGAGE-ZOLD_LAKASHITEL";
	protected readonly componentType: EventComponentType = "small-calculator";

	public static defaultProps = {
		outsideTitle: "Zöld lakáshitel kalkulátor",
		outsideTitleLink: "/zold-hitel/",
		bottomLogo: true,
	};

	protected getInitialState(): State {
		return {
			...super.getInitialState(),
			activeStep: "step-1",
			errorMessage: undefined,
			response: {
				...DEFAULT_RESPONSE,
			},
		};
	}

	protected renderSteps(): ReactNode {
		const {
			submitState,
			values: { email },
			response: {
				loanAmount,
				installmentMinAmount,
				installmentMaxAmount,
				loanMax70mEntitled,
				necesseryLoadAmount,
				calc3GreenLoanInstallment,
				calc3GreenLoanEntitled,
			},
			errorMessage,
		} = this.state;

		return (
			<>
				<Step.Step1 stepName="step-1" defaultTarget="step-2" />
				<Step.Step2 stepName="step-2" defaultTarget="result" canStepBack />
				<Step.Result
					stepName="result"
					defaultTarget="lead-form"
					loanAmount={loanAmount}
					installmentMinAmount={installmentMinAmount}
					installmentMaxAmount={installmentMaxAmount}
					loanMax70mEntitled={loanMax70mEntitled}
					necesseryLoadAmount={necesseryLoadAmount}
					installmentWithoutCSOK={calc3GreenLoanEntitled ? calc3GreenLoanInstallment : undefined}
					canStepBack
				/>
				<Step.ResultError stepName="result-error" canStepBack error={errorMessage} />
				<Step.LeadForm
					stepName="lead-form"
					defaultTarget="success-page"
					submitState={submitState}
					canStepBack
				/>
				<Step.SuccessPage stepName="success-page" subscriptionTypes={["RETAIL_NEWSLETTER"]} userEmail={email} />
			</>
		);
	}

	protected onStep(target: string, withEvent: boolean): void {
		switch (target) {
			case "result":
				this.onResult(target);
				break;
			case "success-page":
				this.onSuccessPage();
				break;
			default:
				super.onStep(target, withEvent);
		}
	}

	private onResult(target: string): void {
		const {
			values: { wantsNewHouse, bbClassification, loanAmount, realEstateValue, income, inKhr },
		} = this.state;

		const searchParams: GreenLoanSmallCalculatorRequest = {
			wantsNewHouse,
			bbClassification,
			loanAmount,
			realEstateValue,
			income,
			inKhr,
		};
		const { errorMessage, response } = calculate(searchParams);

		if (errorMessage !== null) {
			this.setState(
				{
					errorMessage,
				},
				() => super.onStep("result-error", true),
			);
		} else {
			this.setState(
				{
					response,
				},
				() => super.onStep(target, true),
			);
		}

		this.sendCalculationEvent(errorMessage ? [errorMessage] : []);
	}

	protected onSuccessPage(): void {
		this.sendLead()
			.then((_response) => {
				this.setState({ submitState: "done" });

				this.promisePool.addTimeout().then(() => {
					this.setState({ submitState: undefined });
				});

				super.onStep("success-page", false);
			})
			.catch((error: Error) => {
				// TODO: Hibakezelés
				logError(error);
			});
	}

	protected getCalculatorRequest(): GreenLoanSmallCalculatorRequest {
		const {
			values: { loanAmount, question, productRequisitionTime, realEstateValue, income, inKhr },
		} = this.state;

		const request = {
			...super.getCalculatorRequest(),
			question,
			productRequisitionTime,
			loanAmount,
			realEstateValue,
			incomeList: [
				{
					incomeType: "SALARY-H",
					incomeTypeName: "Magyar alkalmazotti jövedelem",
					amount: income,
					receiptAmount: income,
				},
			],
			inKhr,
		};

		return request;
	}

	protected getLeadDataRequest(): GreenLoanSmallCalculatorRequest {
		const {
			values: { wantsNewHouse, bbClassification },
		} = this.state;
		const request = this.getCalculatorRequest();

		const otherData = new JsonDataBuilder(request.otherDataJson);
		otherData.addItem("Új lakást szeretnél vásárolni, vagy építkeznél?", "wantsNewHouse", wantsNewHouse, "bool");
		otherData.addItem(
			"Alacsony energiaigényű, energiahatékony ingatlant veszel, építesz?",
			"bbClassification",
			bbClassification,
			"bool",
		);

		request.otherDataJson = otherData.getJson();

		return request;
	}

	protected getLeadData(): LeadRequest<GreenLoanSmallCalculatorRequest, GreenLoanSmallCalculatorResponse> {
		const data = super.getLeadData();
		data.productSubType = "ZOLD_LAKASHITEL";

		return data;
	}

	protected getAutoresponderName(): string {
		return "autoresponder-green-loan-mortgages";
	}

	protected getAutoresponderFields(): Record<string, any> {
		const {
			values: { firstName, lastName },
			response,
		} = this.state;

		return {
			lastName,
			firstName,

			loanAmount: response.loanAmount,
			loanAmountText: this.formatCurrencyForAutoresponder(response.loanAmount),
			loanMax70mEntitled: response.loanMax70mEntitled,
			necesseryLoadAmountText: this.formatCurrencyForAutoresponder(response.necesseryLoadAmount),

			calc1CsokEntitled: response.calc1CsokEntitled,
			calc1CsokLoanAmountText: this.formatCurrencyForAutoresponder(response.calc1CsokLoanAmount),
			calc1CsokLoanInstallmentText: this.formatCurrencyForAutoresponder(response.calc1CsokLoanInstallment),
			calc1GreenLoanEntitled: response.calc1GreenLoanEntitled,
			calc1GreenLoanLoanAmountText: this.formatCurrencyForAutoresponder(response.calc1GreenLoanLoanAmount),
			calc1GreenLoanInstallmentText: this.formatCurrencyForAutoresponder(response.calc1GreenLoanInstallment),
			calc1GreenLoanLoanAmountSumText: this.formatCurrencyForAutoresponder(response.calc1GreenLoanLoanAmountSum),
			calc1GreenLoanInstallmentSumText: this.formatCurrencyForAutoresponder(
				response.calc1GreenLoanInstallmentSum,
			),

			calc2CsokEntitled: response.calc2CsokEntitled,
			calc2CsokLoanAmountText: this.formatCurrencyForAutoresponder(response.calc2CsokLoanAmount),
			calc2CsokLoanInstallmentText: this.formatCurrencyForAutoresponder(response.calc2CsokLoanInstallment),
			calc2GreenLoanEntitled: response.calc2GreenLoanEntitled,
			calc2GreenLoanInstallmentText: this.formatCurrencyForAutoresponder(response.calc2GreenLoanInstallment),
			calc2GreenLoanLoanAmountText: this.formatCurrencyForAutoresponder(response.calc2GreenLoanLoanAmount),
			calc2GreenLoanLoanAmountSumText: this.formatCurrencyForAutoresponder(response.calc2GreenLoanLoanAmountSum),
			calc2GreenLoanInstallmentSumText: this.formatCurrencyForAutoresponder(
				response.calc2GreenLoanInstallmentSum,
			),

			calc3GreenLoanEntitled: response.calc3GreenLoanEntitled,
			calc3GreenLoanLoanAmountText: this.formatCurrencyForAutoresponder(response.calc3GreenLoanLoanAmount),
			calc3GreenLoanInstallmentText: this.formatCurrencyForAutoresponder(response.calc3GreenLoanInstallment),
			calc3GreenLoanInstallmentSumText: this.formatCurrencyForAutoresponder(
				response.calc3GreenLoanInstallmentSum,
			),
			calc3GreenLoanLoanAmountSumText: this.formatCurrencyForAutoresponder(response.calc3GreenLoanLoanAmountSum),

			installmentMaxAmountText: this.formatCurrencyForAutoresponder(response.installmentMaxAmount),
			installmentMaxAmount: response.installmentMaxAmount,
			installmentMinAmountText: this.formatCurrencyForAutoresponder(response.installmentMinAmount),
			calc1CsokOrGreenLoanEntitled: response.calc1CsokEntitled || response.calc1GreenLoanEntitled,
			calc2CsokOrGreenLoanEntitled: response.calc2CsokEntitled || response.calc2GreenLoanEntitled,
		};
	}

	private formatCurrencyForAutoresponder(value: number): string {
		return formatText(value, "ft").replace(" Ft", "");
	}

	protected getProductType(): ProductType {
		return "MORTGAGE";
	}

	protected getClassName(): string {
		return `${super.getClassName()} green-loan`;
	}

	protected getComponentName(): string {
		return "GreenLoanSmallCalculator";
	}
}

export default GreenLoanSmallCalculator;
